import { QUERY_RESPONSE_RECEIVED } from '@web-platform/shared-universal-constants/src/network';
import { ADS_CAPABILITY_HOMEPAGE } from '@web-platform/shared-universal-types/src/network/requestTypes';

import { INITIAL_STATE as GRAPHIC_PROMOS_INITIAL_STATE } from '../graphicPromos';
import { INITIAL_STATE as HOMEPAGE_HEROES_INITIAL_STATE } from '../homepageHeroes';
import { INITIAL_STATE as HOMEPAGE_VIDEOS_INITIAL_STATE } from '../homepageVideos';
import { transformHomepageAdsCapabilityResponse } from '../transformHomepageAdsCapabilityResponse';

import type { AdsCapabilityHomepagePayloadResponse } from '@web-platform/shared-universal-types/src/network/types';
import type {
  Action,
  HomepageAdsCapabilitySliceUpdates,
} from '@web-platform/shared-universal-types/src/redux/actions/actions-shared-types';
import type { CreativesState } from '@web-platform/shared-universal-types/src/redux/banana/state/combinedResults/entitiesSearchResults/adverts/creatives';

const CREATIVES_LOADED = 'CREATIVES_LOADED';
const CREATIVES_QUERY_REQUIRED = 'CREATIVES_QUERY_REQUIRED';
const CREATIVES_QUERY_IN_FLIGHT = 'CREATIVES_QUERY_IN_FLIGHT';
const CREATIVES_QUERY_ERROR = 'CREATIVES_QUERY_ERROR';
const CREATIVES_QUERY_TIMED_OUT = 'CREATIVES_QUERY_TIMED_OUT';

export const INITIAL_STATE: CreativesState = {
  graphicPromos: GRAPHIC_PROMOS_INITIAL_STATE,
  homepageHeroes: HOMEPAGE_HEROES_INITIAL_STATE,
  homepageVideos: HOMEPAGE_VIDEOS_INITIAL_STATE,
  status: 'starting',
};

const isAdsCapabilityHomepageErrorResponse = (
  response: AdsCapabilityHomepagePayloadResponse,
): response is AdsCapabilityHomepagePayloadResponse & { error: true } => response.error;

// Each ARS response represents the complete set of ads for a given view, so it
// REPLACES the existing creatives rather than merging. Merging caused graphic promos
// to accumulate across repeated homepage views (2, 3, 4...) once the per-view query key
// fix made ARS re-fire on every soft-nav.
const replaceHomepageAdsCapabilityUpdates = (
  updates: HomepageAdsCapabilitySliceUpdates,
): CreativesState => ({
  graphicPromos: {
    byId: { ...updates.graphicPromos },
  },
  homepageHeroes: {
    byId: { ...updates.homepageHeroes },
  },
  homepageVideos: {
    byId: { ...updates.homepageVideos },
  },
  status: 'ready',
});

const creativesReducer = (
  state: CreativesState = INITIAL_STATE,
  action: Action,
): CreativesState => {
  switch (action.type) {
    case QUERY_RESPONSE_RECEIVED: {
      if (action.payload.type === ADS_CAPABILITY_HOMEPAGE) {
        if (isAdsCapabilityHomepageErrorResponse(action.payload.response)) {
          return state;
        }

        return replaceHomepageAdsCapabilityUpdates(
          transformHomepageAdsCapabilityResponse(action.payload.response.body),
        );
      }
      return state;
    }

    case CREATIVES_LOADED:
      return replaceHomepageAdsCapabilityUpdates(
        transformHomepageAdsCapabilityResponse(action.data),
      );

    case CREATIVES_QUERY_REQUIRED:
    case CREATIVES_QUERY_IN_FLIGHT:
      return {
        ...state,
        status: 'loading',
      };

    case CREATIVES_QUERY_ERROR:
      return {
        ...state,
        status: 'error',
      };

    case CREATIVES_QUERY_TIMED_OUT:
      return {
        ...state,
        status: 'timeout',
      };

    default:
      return state;
  }
};
export default creativesReducer;
